import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import OverdueTasksWidget from '../components/DashboardWidgets/OverdueTasksWidget';
import RecentTasksWidget from '../components/DashboardWidgets/RecentTasksWidget';
import WeeklyCalendarWidget from '../components/DashboardWidgets/WeeklyCalendarWidget';
import QuickActionsWidget from '../components/DashboardWidgets/QuickActionsWidget';
import TodaysNotesWidget from '../components/DashboardWidgets/TodaysNotesWidget';
import RecentLeadsWidget from '../components/DashboardWidgets/RecentLeadsWidget';

const DASHBOARD_LAYOUT_KEY = 'dashboard-layout';

const defaultColumns = {
  col1: ['weeklyCalendar', 'quickActions'],
  col2: ['overdue', 'recent' ],
  col3: ['recentLeads','todaysNotes' ],
};

const DashboardPage: React.FC = () => {

  const [columns, setColumns] = useState(defaultColumns);

  // Load layout from localStorage on component mount
  useEffect(() => {
    try {
      const savedLayout = localStorage.getItem(DASHBOARD_LAYOUT_KEY);
      if (savedLayout) {
        const parsedLayout = JSON.parse(savedLayout);
        setColumns(parsedLayout);
      }
    } catch (error) {
      console.error('Error loading dashboard layout from localStorage:', error);
      // If there's an error, keep the default layout
    }
  }, []);

  // Save layout to localStorage whenever columns change
  useEffect(() => {
    try {
      localStorage.setItem(DASHBOARD_LAYOUT_KEY, JSON.stringify(columns));
    } catch (error) {
      console.error('Error saving dashboard layout to localStorage:', error);
    }
  }, [columns]);

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;

    const sourceColumn = [...columns[source.droppableId as keyof typeof columns]];
    const destColumn = [...columns[destination.droppableId as keyof typeof columns]];
    const [removed] = sourceColumn.splice(source.index, 1);

    if (source.droppableId === destination.droppableId) {
      sourceColumn.splice(destination.index, 0, removed);
      setColumns({ ...columns, [source.droppableId]: sourceColumn });
    } else {
      destColumn.splice(destination.index, 0, removed);
      setColumns({
        ...columns,
        [source.droppableId]: sourceColumn,
        [destination.droppableId]: destColumn,
      });
    }
  };

  const renderWidget = (id: string) => {
    switch (id) {
      case 'weeklyCalendar':
        return <WeeklyCalendarWidget />;
      case 'overdue':
        return <OverdueTasksWidget />;
      case 'recent':
        return <RecentTasksWidget />;
      case 'quickActions':
        return <QuickActionsWidget />;
      case 'recentLeads':
        return <RecentLeadsWidget />;
      case 'todaysNotes':
        return <TodaysNotesWidget />;
      default:
        return (
            <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>
              <Typography variant="h6" fontSize="0.95rem" fontWeight={500}>
                Widget {id}
              </Typography>
              <Typography variant="body2" fontSize="0.8rem" color="text.secondary">
                Tutaj możesz dodać własną treść.
              </Typography>
            </Box>
        );
    }
  };

  return (
      <Box>
        <DragDropContext onDragEnd={onDragEnd}>
          <Grid container spacing={3}>
            {Object.entries(columns).map(([colId, widgetIds]) => (
                <Grid key={colId} size={{xs: 12, md: 4}}>
                  <Droppable droppableId={colId} isDropDisabled={false} isCombineEnabled={false} ignoreContainerClipping={false}>
                    {(provided) => (
                        <Box
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            sx={{
                              minHeight: 400,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 2,
                            }}
                        >
                          {widgetIds.map((widgetId, index) => (
                              <Draggable key={widgetId} draggableId={widgetId} index={index}>
                                {(provided) => (
                                    <Box
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        sx={{ userSelect: 'none' }}
                                    >
                                      {renderWidget(widgetId)}
                                    </Box>
                                )}
                              </Draggable>
                          ))}
                          {provided.placeholder}
                        </Box>
                    )}
                  </Droppable>
                </Grid>
            ))}
          </Grid>
        </DragDropContext>
      </Box>
  );
};

export default DashboardPage;
